import { NOTIFICATION_FILTER_FIELD, NOTIFICATION_VIEW, READ_STATE, sourcesForArea } from './notificationCenterViews'
import { RELATIVE_RANGE_LABELS, rangeFor, type RelativeRange } from './notificationDateOperators'

import type { GridFilterModel } from '@mui/x-data-grid-pro'
import type {
  NotificationCategory,
  NotificationListParams,
  NotificationSource,
  NotificationStatus,
} from 'types/modules/notification-center/notification'

/** The panel's date operators carry a Date; the contract wants a plain ISO day. */
const asIsoDay = (value: unknown): string | undefined => {
  const date = value instanceof Date ? value : undefined
  if (!date || Number.isNaN(date.getTime())) return undefined
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

const asList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter(entry => typeof entry === 'string')
  return typeof value === 'string' && value ? [value] : []
}

/** The view pins one dimension; the panel refines the rest. Both end up on the query. */
const paramsForView = (viewKey: string, areas: readonly string[]): NotificationListParams => {
  if (viewKey === NOTIFICATION_VIEW.UNREAD) return { read: false }
  if (viewKey.startsWith('AREA_')) {
    const area = viewKey.slice('AREA_'.length)
    // The key is the uppercased area, so match case-insensitively rather than re-deriving it.
    const matched = areas.find(name => name.toUpperCase() === area)
    return matched ? { source: sourcesForArea(matched) } : {}
  }
  if (viewKey.startsWith('CATEGORY_')) return { category: [viewKey.slice('CATEGORY_'.length) as NotificationCategory] }
  return {}
}

/**
 * Folds the rail's view, the search box and the grid's own filter model into one set
 * of query params. Every dimension the contract accepts is sent, so nothing is left
 * for the client to filter and the counts come from the server.
 */
export const buildNotificationListParams = (
  viewKey: string,
  query: string,
  filterModel: GridFilterModel | undefined,
  areas: readonly string[]
): NotificationListParams => {
  const params: NotificationListParams = { ...paramsForView(viewKey, areas) }

  const trimmed = query.trim()
  if (trimmed) params.search = trimmed
  ;(filterModel?.items ?? []).forEach(item => {
    switch (item.field) {
      case NOTIFICATION_FILTER_FIELD.STATUS: {
        const values = asList(item.value) as NotificationStatus[]
        if (values.length) params.status = values
        break
      }
      case NOTIFICATION_FILTER_FIELD.CATEGORY: {
        const values = asList(item.value) as NotificationCategory[]
        if (values.length) params.category = values
        break
      }
      case NOTIFICATION_FILTER_FIELD.AREA: {
        const selectedAreas = asList(item.value)
        const sources = selectedAreas.flatMap(sourcesForArea)
        if (sources.length) params.source = sources as NotificationSource[]
        break
      }
      /**
       * The contract takes one `search`, so the panel's text filter is the same dimension as the toolbar
       * box. It is applied after, and therefore wins: a filter the user just built has to visibly do
       * something, and silently losing to the box is the bug this replaced.
       */
      case NOTIFICATION_FILTER_FIELD.TEXT: {
        const term = typeof item.value === 'string' ? item.value.trim() : ''
        if (term) params.search = term
        break
      }
      case NOTIFICATION_FILTER_FIELD.READ: {
        const [state] = asList(item.value)
        if (state === READ_STATE.READ) params.read = true
        if (state === READ_STATE.UNREAD) params.read = false
        break
      }
      case 'occurredAt': {
        if (item.operator === 'isWithin') {
          const range = item.value as RelativeRange | undefined
          if (range && RELATIVE_RANGE_LABELS[range]) {
            const { start, end } = rangeFor(range)
            params.occurredFrom = asIsoDay(start)
            params.occurredTo = asIsoDay(end)
          }
          break
        }
        const day = asIsoDay(item.value)
        if (!day) break
        if (item.operator === 'onOrAfter' || item.operator === 'after') params.occurredFrom = day
        else if (item.operator === 'onOrBefore' || item.operator === 'before') params.occurredTo = day
        else {
          params.occurredFrom = day
          params.occurredTo = day
        }
        break
      }
      default:
        break
    }
  })

  return params
}
